import React, { useEffect, useMemo, useRef } from 'react';
import { Group, Vector3 } from 'three';
import LocationSphere from '../spheres/LocationSphere';
import { Colors } from '../../../data/Colors';
import DetailedSphere from '../spheres/DetailedSphere';
import useAspectScale from '../hooks/useAspectScale';
import useDetailSectionAnimation from '../../../hooks/useDetailSectionAnimation';
import { DetailSectionIDs } from '../../../data/Sections';
import FocusPoints from '../../../data/FocusPoints';
import { useDetailViewContext } from '../../../context/DetailViewContext';
import {SceneSection} from '../View3D';

const drift = new Vector3(0, 0.2, -0.3);
const husbandPos = new Vector3(0.97, -0.3, 0.3);
const forestPos = new Vector3(0.885, -0.2, 0.3);

const origin = new Vector3();

export default function TopologicalRelations({ groupProps, texts }: SceneSection): JSX.Element
{
  const sphereRef = useRef<Group>(null);
  const husbandRef = useRef<Group>(null);
  const scale = useAspectScale(1.4);
  const animPhase = useMemo(() =>
  {
    return { value: 0 };
  }, []);
  const animation = useDetailSectionAnimation(DetailSectionIDs.DecodingTopology, FocusPoints.TopologicalHusband);

  useEffect(() =>
  {
    animation.clear(true);

    function onUpdate() {
      const sphere = sphereRef.current;
      const husband = husbandRef.current;

      if (sphere && husband) {
        sphere.position.copy(forestPos).addScaledVector(drift, animPhase.value);
        husband.position.copy(husbandPos).addScaledVector(drift, animPhase.value);
      }
    }

    animation.fromTo(animPhase, { value: 1 }, {
      value: 0,
      duration: 2,
      delay: 1,
      immediateRender: true,
      onUpdate
    });
    animation.fromTo(animPhase, { value: 0 }, {
      value: 1,
      duration: 2,
      delay: 3,
      immediateRender: false,
      onUpdate
    });

    animation.repeat(-1);
  }, [ animPhase, animation ]);

  const {activeDetailID, activeFocusID} = useDetailViewContext();

  useEffect(() => {
    if(activeFocusID !== FocusPoints.None && activeFocusID !== FocusPoints.Overview) {
      animation.pause();
    }else {
      animation.play();
    }
  }, [activeFocusID, animation]);

  return <group {...groupProps} scale={[ scale, scale, scale ]} visible={activeFocusID === FocusPoints.None || activeDetailID === DetailSectionIDs.DecodingTopology}>
    <LocationSphere label={texts.home} sphereSize={0.5} sphereCenter={[ -1.0, 0.0, 0 ]} azimuth={0.25}/>

    <LocationSphere label="20 km" sphereSize={0.2} sphereCenter={[ -0.31, 0.0, 0 ]} azimuth={3.0}
      rotation={[ 0.0, -0.2, -Math.PI * .5 ]} flipSide={true}/>

    <LocationSphere label={texts.city} sphereSize={0.17} azimuth={2.0} sphereCenter={[ 0.05, 0.0, 0 ]}/>

    <LocationSphere label={texts.bridge} sphereSize={0.1445} sphereCenter={[ 0.35, 0.0, 0 ]} azimuth={2.5}/>

    <LocationSphere label={texts.forest} sphereSize={0.4} sphereCenter={origin} azimuth={2.0} ref={sphereRef}
      position={forestPos}/>

    <DetailedSphere label={texts.cat} color={Colors.CatVector} arrowAngle={1.8}
      sphereCenter={[ -1.3, -0.3, -0.3 ]} sphereSize={0.2}
      happiness={1.2} winner={-1.0} showHappy
      focusID={FocusPoints.TopoligicalCat}
      texts={texts}
    />

    <DetailedSphere label={texts.husband} color={Colors.HusbandVector}
      sphereCenter={origin} sphereSize={0.23} arrowAngle={3.7} ref={husbandRef}
      happiness={-1.0} winner={1.0} showHappy position={husbandPos}
      focusID={FocusPoints.TopologicalHusband}
      texts={texts}
    />

    <DetailedSphere label={texts.wife} color={Colors.WifeVector}
      sphereCenter={[ -0.85, 0.1, 0.0 ]} sphereSize={0.23} arrowAngle={-0.7}
      happiness={1.0} winner={1.0} showHappy
      focusID={FocusPoints.TopoligicalWife}
      texts={texts}
    />
  </group>;
}
